import { useMemo, useState, useCallback } from 'react';
import moment from 'moment';

import { Nullable, SelectOption } from '~globals/types';
import { DriverCompanyBase } from '~services/driver/types';
import { VehicleBase } from '~services/vehicle/types';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { ItemFilter } from '~services/item/types';
import { RouteUserBasic } from '~services/route/types';

import { FiltersValuesReturn, useFiltersValues } from './useFiltersValues';
import { useSubscriptionFeatures } from './useSubscriptionFeatures';
import { RouteItemStateType, PlanFeaturesTypes } from '~globals/types/enums';
import { getItemValue, formatterDate } from '~utils/index';
import {
  ROUTE_ITEM_STATE_KEY,
  ITEM_STATE_KEY,
} from '~constants/ordersStatesOptions';

export interface OrdersSearchFilters {
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;
  search: string;
  expirationDate: Nullable<Date>;
  states: string[];
  drivers: Nullable<DriverCompanyBase[]>;
  vehicles: Nullable<VehicleBase[]>;
  services: Nullable<SelectOption[]>;
  zones: Nullable<LogisticZoneBase[]>;
  priceGt: string;
  priceLt: string;
  assignedUser: Nullable<RouteUserBasic>;
}

export interface OrdersSearchFiltersParams {
  initialFilters: OrdersSearchFilters;
}

export interface OrdersSearchFiltersReturn
  extends FiltersValuesReturn<OrdersSearchFilters> {
  parseFiltersRequest: (
    currentFilters?: Partial<OrdersSearchFilters>,
  ) => ItemFilter;
  filtersActives: OrdersSearchFilters;
  setFiltersActives: (newFiltersActives: OrdersSearchFilters) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilterActiveByKey: (key: string, newVal: any) => void;
  clearFiltersActives: () => void;
}

export const useOrdersSearchFilters = ({
  initialFilters,
}: OrdersSearchFiltersParams): OrdersSearchFiltersReturn => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccesLimitNotPro = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
    [hasAccessToFeature],
  );

  const hasAccesMgmZones = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [hasAccessToFeature],
  );

  const filters = useFiltersValues(initialFilters);

  const [filtersActives, setFiltersActives] = useState<OrdersSearchFilters>(
    filters.filters,
  );

  const setFilterActiveByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, newVal: any) => {
      setFiltersActives((prevActiveFilters) => ({
        ...prevActiveFilters,
        [key]: newVal,
      }));
    },
    [],
  );

  const clearFiltersActives = useCallback(() => {
    setFiltersActives(initialFilters);
  }, [initialFilters]);

  const getValueCurrentFilter = useCallback(
    <Key extends keyof OrdersSearchFilters>(
      key: Key,
    ): OrdersSearchFilters[Key] => getItemValue(filters.filters, key),
    [filters.filters],
  );

  const parseDateForRequest = useCallback(
    ({
      currentKey,
      currentDate,
    }: {
      currentKey: 'dateFrom' | 'dateTo' | 'expirationDate';
      currentDate?: Nullable<Date>;
    }): Nullable<string> => {
      let date = getValueCurrentFilter(currentKey);

      if (currentDate !== undefined) {
        date = currentDate;
      }

      if (date !== null) {
        const formatDate = formatterDate(new Date(date), {
          format: moment.HTML5_FMT.DATETIME_LOCAL_SECONDS,
        });

        return formatDate || null;
      }

      return null;
    },
    [getValueCurrentFilter],
  );

  const parseSearchForRequest = useCallback(
    (currentSearch?: string): Nullable<string> => {
      let search = getValueCurrentFilter('search');

      if (currentSearch !== undefined) {
        search = currentSearch;
      }

      return search || null;
    },
    [getValueCurrentFilter],
  );

  const parseSelectedStates = useCallback(
    (states: string[]): string[][] => states.map((state) => state.split('-')),
    [],
  );

  const filterStatesbyPrefix = useCallback(
    (states: string[][], compratePrefix: string): number[] =>
      states
        .filter(([prefix]) => prefix === compratePrefix)
        .map(([, currentState]) => Number(currentState)),
    [],
  );

  const parseRouteStatesForRequest = useCallback(
    (currentStates?: string[]) => {
      let states = getValueCurrentFilter('states') ?? [];

      if (currentStates !== undefined) {
        states = currentStates ?? [];
      }

      const parseStates = parseSelectedStates(states);

      const filterStates = filterStatesbyPrefix(
        parseStates,
        ROUTE_ITEM_STATE_KEY,
      );

      const includeNewState = parseStates.some(
        ([prefix]) => prefix === ITEM_STATE_KEY,
      );

      if (includeNewState) {
        return [RouteItemStateType.New, ...filterStates];
      }

      return filterStates;
    },
    [getValueCurrentFilter, parseSelectedStates, filterStatesbyPrefix],
  );

  const parseItemStatesForRequest = useCallback(
    (currentStates?: string[]) => {
      let states = getValueCurrentFilter('states') ?? [];

      if (currentStates !== undefined) {
        states = currentStates ?? [];
      }

      const parseStates = parseSelectedStates(states);

      const filterStates = filterStatesbyPrefix(parseStates, ITEM_STATE_KEY);

      return filterStates;
    },
    [getValueCurrentFilter, parseSelectedStates, filterStatesbyPrefix],
  );

  const parseDriversForRequest = useCallback(
    (currentDrivers?: Nullable<DriverCompanyBase[]>) => {
      let drivers = getValueCurrentFilter('drivers');

      if (currentDrivers !== undefined) {
        drivers = currentDrivers;
      }

      if (drivers && drivers.length > 0) {
        return drivers.map((driver) => driver.id);
      }

      return null;
    },
    [getValueCurrentFilter],
  );

  const parseVehiclesForRequest = useCallback(
    (currentVehicles?: Nullable<VehicleBase[]>) => {
      let vehicles = getValueCurrentFilter('vehicles');

      if (currentVehicles !== undefined) {
        vehicles = currentVehicles;
      }

      if (vehicles && vehicles.length > 0) {
        return vehicles.map((vehicle) => vehicle.id);
      }

      return null;
    },
    [getValueCurrentFilter],
  );

  const parseServicesForRequest = useCallback(
    (currentServices?: Nullable<SelectOption[]>) => {
      if (!hasAccesLimitNotPro) return null;

      let services = getValueCurrentFilter('services');

      if (currentServices !== undefined) {
        services = currentServices;
      }

      if (services && services.length > 0) {
        return services.map((service) => service.value.toString());
      }

      return null;
    },
    [hasAccesLimitNotPro, getValueCurrentFilter],
  );

  const parseZonesForRequest = useCallback(
    (currentZones?: Nullable<LogisticZoneBase[]>) => {
      if (!hasAccesMgmZones) return null;

      let zones = getValueCurrentFilter('zones');

      if (currentZones !== undefined) {
        zones = currentZones;
      }

      if (zones && zones.length > 0) {
        return zones.map((zone) => zone.zoneId);
      }

      return null;
    },
    [hasAccesMgmZones, getValueCurrentFilter],
  );

  const parsePriceForRequest = useCallback(
    ({
      currentKey,
      currentPrice,
    }: {
      currentKey: 'priceGt' | 'priceLt';
      currentPrice?: string;
    }): Nullable<number> => {
      let price = getValueCurrentFilter(currentKey);

      if (currentPrice !== undefined) {
        price = currentPrice;
      }

      return price !== '' && price !== null ? Number(price) : null;
    },
    [getValueCurrentFilter],
  );

  const parseAssignedUserForRequest = useCallback(
    (currentAssignedUser?: Nullable<RouteUserBasic>) => {
      if (!hasAccesLimitNotPro) return null;

      let assignedUser = getValueCurrentFilter('assignedUser');

      if (currentAssignedUser !== undefined) {
        assignedUser = currentAssignedUser;
      }

      if (assignedUser) {
        return assignedUser.userId;
      }

      return null;
    },
    [getValueCurrentFilter, hasAccesLimitNotPro],
  );

  const parseFiltersRequest = useCallback(
    (currentFilters?: Partial<OrdersSearchFilters>): ItemFilter => ({
      dateFrom: parseDateForRequest({
        currentKey: 'dateFrom',
        currentDate: currentFilters?.dateFrom,
      }),
      dateTo: parseDateForRequest({
        currentKey: 'dateTo',
        currentDate: currentFilters?.dateTo,
      }),
      routeCode: parseSearchForRequest(currentFilters?.search),
      maxDeliveredDateTime: parseDateForRequest({
        currentKey: 'expirationDate',
        currentDate: currentFilters?.expirationDate,
      }),
      routeItemStateTypesId: parseRouteStatesForRequest(currentFilters?.states),
      collectItemStateTypesId: parseItemStatesForRequest(
        currentFilters?.states,
      ),
      driversId: parseDriversForRequest(currentFilters?.drivers),
      vehiclesId: parseVehiclesForRequest(currentFilters?.vehicles),
      serviceTypeList: parseServicesForRequest(currentFilters?.services),
      zonesId: parseZonesForRequest(currentFilters?.zones),
      unit1GraterThan: null,
      unit1LowerThan: null,
      unit2GraterThan: null,
      unit2LowerThan: null,
      priceGraterThan: parsePriceForRequest({
        currentKey: 'priceGt',
        currentPrice: currentFilters?.priceGt,
      }),
      priceLowerThan: parsePriceForRequest({
        currentKey: 'priceLt',
        currentPrice: currentFilters?.priceLt,
      }),
      assignedUserId: parseAssignedUserForRequest(currentFilters?.assignedUser),
    }),
    [
      parseDateForRequest,
      parseSearchForRequest,
      parseRouteStatesForRequest,
      parseItemStatesForRequest,
      parseDriversForRequest,
      parseVehiclesForRequest,
      parseServicesForRequest,
      parseZonesForRequest,
      parsePriceForRequest,
      parseAssignedUserForRequest,
    ],
  );

  return {
    ...filters,
    parseFiltersRequest,
    filtersActives,
    setFiltersActives,
    setFilterActiveByKey,
    clearFiltersActives,
  };
};
